import { useEffect, useMemo } from 'react';

import type { AvatarSessionContext } from '../../session';

const LIVE_WEBRTC_USER_ID_STORAGE_KEY = 'camlin.live.webrtcUserId';

function createStableLiveProvisionUserId(prefix: string, maxLength: number): string {
  const suffixLength = Math.max(1, maxLength - prefix.length);
  let suffix = '';

  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    suffix = crypto.randomUUID().replace(/-/g, '');
  }

  while (suffix.length < suffixLength) {
    suffix += Math.random().toString(36).slice(2);
  }

  return `${prefix}${suffix.slice(0, suffixLength)}`;
}

export interface UseLiveProvisionIdentityOptions {
  isVisualOnly: boolean;
  liveModeActive: boolean;
  liveSessionContext: AvatarSessionContext | null;
  liveVoiceCallEnabled: boolean;
  noSip: boolean;
  queryParams: URLSearchParams;
}

export interface UseLiveProvisionIdentityReturn {
  liveProvisionUserId: string | undefined;
}

export function useLiveProvisionIdentity({
  isVisualOnly,
  liveModeActive,
  liveSessionContext,
  liveVoiceCallEnabled,
  noSip,
  queryParams
}: UseLiveProvisionIdentityOptions): UseLiveProvisionIdentityReturn {
  const effectiveNoSip = liveModeActive ? isVisualOnly : (noSip || isVisualOnly);

  const liveProvisionUserId = useMemo(() => {
    if (!liveModeActive || effectiveNoSip) {
      return undefined;
    }

    if (liveSessionContext?.userId) {
      return liveSessionContext.userId;
    }

    const queryUserId = queryParams.get('userId');
    if (queryUserId) {
      return queryUserId;
    }

    // CamlinVoice persists auth ids as `${endpointId}-auth`, so the fallback live
    // user id must stay comfortably under the backend's effective 22-char limit.
    // Use a stable browser-scoped id so live mode reuses a single endpoint instead
    // of leaking one new PJSIP identity per session token.
    const generatedUserIdPrefix = 'avatar-';
    const maxGeneratedUserIdLength = 20;
    try {
      const storedUserId = window.localStorage.getItem(LIVE_WEBRTC_USER_ID_STORAGE_KEY)?.trim();
      if (storedUserId) {
        return storedUserId;
      }

      const generatedUserId = createStableLiveProvisionUserId(
        generatedUserIdPrefix,
        maxGeneratedUserIdLength
      );
      window.localStorage.setItem(LIVE_WEBRTC_USER_ID_STORAGE_KEY, generatedUserId);
      return generatedUserId;
    } catch (error) {
      console.warn('[LiveMode] Failed to persist WebRTC identity', error);
    }

    return createStableLiveProvisionUserId(generatedUserIdPrefix, maxGeneratedUserIdLength);
  }, [
    effectiveNoSip,
    liveModeActive,
    liveSessionContext?.userId,
    queryParams
  ]);

  useEffect(() => {
    if (!liveModeActive || !liveVoiceCallEnabled || !liveProvisionUserId) {
      return;
    }

    const source = liveSessionContext?.userId
      ? 'session-context'
      : queryParams.get('userId')
        ? 'query-param'
        : 'browser-stable';

    const identityInfo = {
      userId: liveProvisionUserId,
      source,
      handoffToken: liveSessionContext?.handoffToken ?? null,
      sessionId: liveSessionContext?.sessionId ?? null
    };

    console.log('[LiveMode] WebRTC identity selected', identityInfo);
    (window as any).__CAMLIN_LIVE_WEBRTC_IDENTITY__ = identityInfo;
  }, [
    liveModeActive,
    liveProvisionUserId,
    liveSessionContext?.handoffToken,
    liveSessionContext?.sessionId,
    liveSessionContext?.userId,
    liveVoiceCallEnabled,
    queryParams
  ]);

  return {
    liveProvisionUserId
  };
}

export default useLiveProvisionIdentity;
